import {
  convertPercentageToMarks,
  deserify,
  getLocalStorage,
  Loader,
  NoContentCard,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { useAppDispatch, useAppSelector } from '../../../reduxStore/reduxHooks';
import {
  AttemptTypeEnum,
  SessionStatusEnum,
} from '@protos/learning_management/lms.db_pb';
import { ClickerAssessmentOngoingHw } from '../ClickerAssessmentOngoingHw';
import { ClickerAssessmentEndedHw } from '../ClickerAssessmentEndedHw';
import { setCurrentAssessmentClassStats } from '../reducer/manageHomework.slice';
import { useEffect, useState } from 'react';
import { IStatusObj } from '../OngoingHomework';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import {
  resetClickerAssessmentState,
  setClickerAssessmentInfo,
} from '../../Assessment/ClickerAssessment/reducer/clickerAssessment.slice';
import { isTimeLeft } from '../manageHWutils';
import {
  HOME,
  HOMEWORK_MANAGE,
} from '../../../routeHandling/RoutesNomenclature';
import { useLocation, useNavigate } from 'react-router-dom';

export type AssessmentPageType =
  | 'connected-assess-active'
  | 'connected-assess-ended'
  | 'assessment-active'
  | 'assessment-ended';

export const ClickerAssessmentManage = () => {
  const dispatch = useAppDispatch();
  const teacher_id = getLocalStorage('userId');
  const { LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler } =
    useCommonServiceClientContext();
  const { selected_assessment_id } = deserify(
    useAppSelector((state) => state.manageHomework)
  );
  const { clicker_assessment_info } = deserify(
    useAppSelector((state) => state.clickerAssessment)
  );
  const { setSelectedFunction, setSelectedLogoClickHandler } =
    useGlobalContext();
  const [isAssessmentOngoing, setIsAssessmentOngoing] = useState<
    boolean | null
  >(null);
  const navigate = useNavigate();
  // ----------------------------------------------------------------
  const [loading, setLoading] = useState<'loading' | 'error' | 'success'>(
    'success'
  );
  const location = useLocation();
  const getAssessmentClassStats = async () => {
    setLoading('loading');
    try {
      if (!teacher_id || !selected_assessment_id) {
        setLoading('error');
        setIsAssessmentOngoing(false);
        return;
      }
      const response =
        await LmsAssessmentTeacherAPIServiceV3WithStatusCodeHandler.getAssessmentClassStats(
          {
            teacherId: BigInt(teacher_id),
            assessmentId: selected_assessment_id,
            attemptNumber:
              clicker_assessment_info?.selected_attempt?.attemptNumber,
          }
        );
      if (response.data) {
        // console.log(response.data);
        const StatusObj: IStatusObj = {};
        response.data.studentPerformanceInfo.forEach((e) => {
          const id = e.studentId.toString();
          StatusObj[id] = {
            studentId: e.studentId,
            isSubmitted: e.isSubmitted || false,
            marks: convertPercentageToMarks(
              e.scorePercent,
              response.data?.assessment?.maxMarks
            ),
          };
        });

        dispatch(
          setClickerAssessmentInfo({
            ...clicker_assessment_info,
            statusInfo: StatusObj,
          })
        );

        dispatch(setCurrentAssessmentClassStats(response.data));
        const latestCompletedAttempt = response.data.attemptInfo
          .filter(
            (e) =>
              e.attemptStatus === SessionStatusEnum.SESSION_STATUS_COMPLETED
          )
          .sort((a, b) => {
            return b.attemptNumber - a.attemptNumber;
          })[0];
        if (!clicker_assessment_info?.selected_attempt?.attemptNumber) {
          if (latestCompletedAttempt) {
            dispatch(
              setClickerAssessmentInfo({
                ...clicker_assessment_info,
                selected_attempt: latestCompletedAttempt,
              })
            );
          }
        }
        setIsAssessmentOngoing(
          isTimeLeft(response.data?.assessment?.assessmentTargetDate)
        );
        setLoading('success');
      } else {
        dispatch(setClickerAssessmentInfo(undefined));
        dispatch(setCurrentAssessmentClassStats(undefined));
        setIsAssessmentOngoing(false);
        setLoading('error');
      }
    } catch (err) {
      setLoading('error');
      setIsAssessmentOngoing(false);
      console.log(err);
    }
  };

  // ----------------------------------------------------------------

  useEffect(() => {
    getAssessmentClassStats();
  }, []);

  // ----------------------------------------------------------------
  const backButtonClick = (navigateType: 'back' | 'home') => {
    const from = location.state?.from;
    if (navigateType === 'back') {
      dispatch(resetClickerAssessmentState());
      if (from === '/home') {
        navigate(HOME);
      } else {
        navigate(HOMEWORK_MANAGE);
      }
    } else if (navigateType === 'home') {
      dispatch(resetClickerAssessmentState());
      navigate(HOME);
    }
  };
  useEffect(() => {
    setSelectedFunction(() => () => backButtonClick('back'));
    setSelectedLogoClickHandler(() => () => backButtonClick('home'));

    return () => {
      setSelectedFunction(null);
      setSelectedLogoClickHandler(null);
    };
  }, []);

  if (loading === 'loading' || isAssessmentOngoing === null) {
    return <Loader />;
  }
  if (loading === 'error') {
    return (
      <NoContentCard variant="info" icon="cards" text="No Assessment Found" />
    );
  }
  if (isAssessmentOngoing === true) {
    return (
      <ClickerAssessmentOngoingHw
        pageType="connected-assess-active"
        setIsAssessmentOngoing={setIsAssessmentOngoing}
      />
    );
  }
  // If reached here, loading is 'success' and assessment has ended
  return <ClickerAssessmentEndedHw pageType="connected-assess-ended" />;
};
